import type { Attachment, AttachmentsIndex, Park } from '../types';
import { isNationalPark, isPendingPark, isProvincialPark } from './helpers';

const PUBLIC_PREFIX = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');

const INDEX_URL = `${PUBLIC_PREFIX}/data/attachments-index.json`;

let indexPromise: Promise<AttachmentsIndex> | null = null;

/** 加载附件索引（单例缓存，多组件共用一次请求） */
export function loadAttachmentsIndex(): Promise<AttachmentsIndex> {
  if (!indexPromise) {
    indexPromise = fetch(INDEX_URL, { cache: 'no-cache' })
      .then((res) => {
        if (!res.ok) {
          return { version: 1, generatedAt: '', entries: [] };
        }
        return res.json() as Promise<AttachmentsIndex>;
      })
      .then((data) => {
        if (!data || !Array.isArray(data.entries)) {
          return { version: 1, generatedAt: '', entries: [] };
        }
        return data;
      })
      .catch(() => ({ version: 1, generatedAt: '', entries: [] }));
  }
  return indexPromise;
}

/** 测试或热更新时可清空缓存 */
export function clearAttachmentsIndexCache(): void {
  indexPromise = null;
}

/** 附件在 public/attachments 下的子目录（与磁盘目录名一致） */
export function getAttachmentSubdir(park: Park): string {
  if (isNationalPark(park)) return 'nationalParks';
  if (isProvincialPark(park)) return 'provincialParks';
  if (isPendingPark(park)) return 'pending';
  return 'provincialParks';
}

export function inferAttachmentType(fileName: string): Attachment['type'] | null {
  const m = fileName.toLowerCase().match(/\.([a-z0-9]+)$/);
  if (!m) return null;
  const ext = m[1];
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png') return 'image';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'doc' || ext === 'docx') return 'doc';
  return null;
}

/** 展示用 label：物理文件名第一个「_」之后的部分 */
export function attachmentLabelFromStorageName(storageName: string): string {
  const i = storageName.indexOf('_');
  return i >= 0 ? storageName.slice(i + 1) : storageName;
}

/**
 * 浏览器内可用的附件路径（含 PUBLIC_URL，与 fetch 静态资源一致）
 */
export function buildAttachmentPath(subdir: string, storageFileName: string): string {
  const encoded = encodeURIComponent(storageFileName);
  return `${PUBLIC_PREFIX}/attachments/${subdir}/${encoded}`;
}

function attachmentsFromDiskIndex(park: Park, index: AttachmentsIndex): Attachment[] {
  const subdir = getAttachmentSubdir(park);
  const name = park.name?.trim() ?? '';
  if (!name) return [];

  const out: Attachment[] = [];
  const seen = new Set<string>();

  for (const e of index.entries) {
    if (e.subdir !== subdir || e.parkName !== name) continue;
    const type = inferAttachmentType(e.storageName);
    if (!type) continue;
    const path = buildAttachmentPath(subdir, e.storageName);
    if (seen.has(path)) continue;
    seen.add(path);
    out.push({
      label: e.label,
      path,
      type,
    });
  }

  return out;
}

/**
 * 仅根据「磁盘扫描索引」解析附件，保证列表与 public/attachments 中真实文件一致。
 * 不再合并 JSON「文件」字段，避免把省级名单等错误文案拼到其它园区下产生无效链接。
 */
export function resolveAttachmentsForPark(
  park: Park,
  index: AttachmentsIndex | null | undefined,
): Attachment[] {
  if (!index?.entries?.length) return [];
  const merged = attachmentsFromDiskIndex(park, index);
  merged.sort((a, b) => a.label.localeCompare(b.label, 'zh-Hans-CN'));
  return merged;
}
